// Server-driven availability for templates/generators. The admin toggles
// live in Site Settings; the app reads them at runtime (short TTL) so a
// change takes effect on web + iOS/Android without shipping an update.
// A template can also be limited to certain plans: the server folds anything
// this visitor's plan does not include into the same hidden set, so every
// page keeps filtering on one list.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserToken, userHeaders } from "./userAuth";
import { APP_LIVE_EVENT } from "./appLive";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const CACHE_TTL_MS = 60 * 1000;

// The set depends on who is asking, so the cache belongs to one sign-in.
let cachedSet = null;
let cachedAt = 0;
let cachedFor = null;
let inflight = null;

function cached() {
  return cachedSet && cachedFor === getUserToken() ? cachedSet : null;
}

export async function fetchDisabledGenerators(force = false) {
  const token = getUserToken();
  const have = cached();
  if (!force && have && Date.now() - cachedAt < CACHE_TTL_MS) return have;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/generators/availability?_=${Date.now()}`, { headers: userHeaders() });
      const data = await res.json();
      if (data && data.success) {
        cachedSet = new Set(data.disabled || []);
        cachedAt = Date.now();
        cachedFor = token;
      }
    } catch (e) {
      // network failure: keep whatever we had (fail open, nothing hidden)
    }
    inflight = null;
    return cached() || new Set();
  })();
  return inflight;
}

export function clearGeneratorAvailabilityCache() {
  cachedSet = null;
  cachedAt = 0;
  cachedFor = null;
}

// The hidden generator ids as a Set, and whether the server has answered for
// this sign-in yet (`ready`): a page starts from the module cache so repeat
// mounts don't flash, then refreshes from the server.
export function useGeneratorAccess() {
  const [hidden, setHidden] = useState(() => cached() || new Set());
  const [ready, setReady] = useState(() => !!cached());
  useEffect(() => {
    let alive = true;
    const load = (force) => fetchDisabledGenerators(force).then((s) => { if (!alive) return; setHidden(new Set(s)); setReady(!!cached()); });
    load(false);
    // The plan being operated on moves with a business switch or a new
    // subscription, and with the account itself: the set is fetched again.
    const onLive = (e) => { const r = e?.detail?.reason; if (r === "business-switch" || r === "subscription") load(true); };
    const onUser = () => load(true);
    window.addEventListener(APP_LIVE_EVENT, onLive);
    window.addEventListener("mintslip-user-updated", onUser);
    return () => {
      alive = false;
      window.removeEventListener(APP_LIVE_EVENT, onLive);
      window.removeEventListener("mintslip-user-updated", onUser);
    };
  }, []);
  return { hidden, ready };
}

// Returns a Set of hidden generator ids (see useGeneratorAccess).
export function useDisabledGenerators() {
  return useGeneratorAccess().hidden;
}

// The page gate: once the server has answered, a page whose generators are
// ALL hidden from this visitor (switched off, or limited to plans they are
// not on) is sent back to the /app landing, so a known URL is no way in.
// An empty `ids` never redirects (an embedded form, the hero preview).
// Returns true while the redirect is under way so the page can stay blank.
export function useGeneratorGate(ids) {
  const navigate = useNavigate();
  const { hidden, ready } = useGeneratorAccess();
  const gone = ready && ids.length > 0 && ids.every((id) => hidden.has(id));
  useEffect(() => { if (gone) navigate("/app", { replace: true }); }, [gone]); // eslint-disable-line
  return gone;
}

// The audiences a template/generator can be limited to in the admin card:
// "free" is everyone without a live plan (guests included), the plan tiers
// (their names come from the subscription catalog), and "admin", the team:
// an account signed in with an admin's or active moderator's email.
export const GENERATOR_AUDIENCES = ["free", "express", "pro", "enterprise", "admin"];

// Registry shown in the admin Site Settings card. Ids are what the app
// filters on — keep them stable.
export const GENERATOR_GROUPS = [
  {
    title: "Pay Stub Templates",
    items: [
      { id: "paystub-gusto", label: "Gusto Style" },
      { id: "paystub-workday", label: "Workday Style" },
      { id: "paystub-onpay", label: "OnPay Style" },
    ],
  },
  {
    title: "Resume Templates",
    items: [
      { id: "resume-ats", label: "ATS Optimized" },
      { id: "resume-modern", label: "Modern Professional" },
      { id: "resume-classic", label: "Classic Executive" },
    ],
  },
  {
    title: "Generators",
    items: [
      { id: "paystub", label: "US Pay Stub" },
      { id: "canadian-paystub", label: "Canadian Paystub" },
      { id: "uk-payslip", label: "UK Payslip" },
      { id: "ai-resume", label: "AI Resume Builder" },
      { id: "offer-letter", label: "Offer Letter" },
      { id: "w2", label: "W-2" },
      { id: "w9", label: "W-9" },
      { id: "1099-nec", label: "1099-NEC" },
      { id: "1099-misc", label: "1099-MISC" },
      { id: "schedule-c", label: "Schedule C" },
      { id: "cease-and-desist", label: "Cease & Desist" },
      { id: "power-of-attorney", label: "Power of Attorney" },
      { id: "vehicle-bill-of-sale", label: "Vehicle Bill of Sale" },
      { id: "commercial-lease", label: "Commercial Lease" },
      { id: "utility-bill", label: "Utility Bill" },
      { id: "bank-statement", label: "Accounting Mockup" },
    ],
  },
];

export const TAX_GENERATOR_IDS = ["w2", "w9", "1099-nec", "1099-misc", "schedule-c"];
export const LEGAL_GENERATOR_IDS = ["cease-and-desist", "power-of-attorney", "vehicle-bill-of-sale"];
export const BUSINESS_GENERATOR_IDS = ["commercial-lease", "utility-bill", "bank-statement"];
